import { formatMoney } from "@pages/profile-v2/utils";
import { getUser } from "@utils";
import { useSelector } from "react-redux";

export const mergeChatMessages = (pages, socketChannel) => {
	const byId = {};
	const removedIds = new Set(socketChannel?.removedIds ?? []);

	if (pages?.length) {
		[...pages].reverse().forEach((page) => {
			(page?.messages ?? []).forEach((message) => {
				if (message?.messageId && !removedIds.has(message.messageId)) {
					byId[message.messageId] = message;
				}
			});
		});
	}

	if (socketChannel?.ids?.length) {
		socketChannel.ids.forEach((id) => {
			if (removedIds.has(id)) {
				return;
			}

			const message = socketChannel.byId[id];
			if (message?.messageId) {
				byId[message.messageId] = message;
			}
		});
	}

	return Object.values(byId).sort(
		(a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
	);
};

/**
 * Shared payload from GET chat-enabled (guest) and GET eligibility (logged in).
 * Global UI visibility uses chatEnabled only; send/deposit fields apply when logged in.
 */
export const isChatGloballyEnabled = (payload) => payload?.chatEnabled !== false;

export const canShareToChat = ({
	isLoggedIn,
	isChatEnabled = true,
	eligibility,
	eligibilityLoading = false,
}) => {
	if (!isLoggedIn || !isChatEnabled || eligibilityLoading) {
		return false;
	}

	if (!isChatGloballyEnabled(eligibility) || eligibility?.chatBanned) {
		return false;
	}

	return eligibility?.canSend === true || eligibility?.eligible === true;
};

export const getComposerState = ({ isLoggedIn, eligibility, channelsLoading, accessLoading }) => {
	if (channelsLoading || accessLoading) {
		return "loading";
	}

	if (!isLoggedIn) {
		return "guest";
	}

	if (eligibility?.chatEnabled === false) {
		return "unavailable";
	}

	if (eligibility?.chatBanned) {
		return "banned";
	}

	if (eligibility?.canSend) {
		return "eligible";
	}

	if (eligibility && !eligibility.eligible) {
		return "belowDeposit";
	}

	return "loading";
};

export const DEPOSIT_AMOUNT_PLACEHOLDER = "\uE000";

export const formatDepositRemainingAmount = (remaining, currency) => {
	const normalizedCurrency = String(currency || "")
		.trim()
		.toUpperCase();

	if (!normalizedCurrency) {
		return String(remaining ?? "");
	}

	return formatMoney(remaining, normalizedCurrency);
};

export const getDepositRemaining = (eligibility) => {
	const required = eligibility?.requiredAmount ?? 0;
	const deposited = eligibility?.lifetimeDepositBaseAmount ?? 0;
	const remaining = Math.max(0, required - deposited);
	const roundedRemaining = Math.round(remaining * 100) / 100;
	const currency = eligibility?.baseCurrency ?? "";

	return {
		remaining: roundedRemaining,
		currency,
		formattedAmount: formatDepositRemainingAmount(roundedRemaining, currency),
	};
};

export const getAvatarColor = (username) => {
	if (!username) {
		return undefined;
	}

	let hash = 0;
	for (let i = 0; i < username.length; i += 1) {
		hash = username.charCodeAt(i) + ((hash << 5) - hash);
	}

	const hue = Math.abs(hash) % 360;
	return `hsl(${hue}, 42%, 38%)`;
};

export const getAvatarInitial = (username) => {
	if (!username) {
		return "?";
	}

	return username.charAt(0).toUpperCase();
};

export const MENTION_USERNAME_CHARS = "a-zA-Z0-9_-";
export const MENTION_USERNAME_PATTERN = /^[a-zA-Z0-9_-]{4,32}$/;
export const MENTION_TOKEN_REGEX = new RegExp(`@([${MENTION_USERNAME_CHARS}]{4,32})`, "g");
export const MENTION_QUERY_REGEX = new RegExp(`@([${MENTION_USERNAME_CHARS}]*)$`);

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const normalizeMentionNames = (mentions = []) =>
	[
		...new Set(
			mentions
				.map((mention) => String(mention ?? "").replace(/^@/, ""))
				.filter(isValidMentionUsername)
		),
	].sort((a, b) => b.length - a.length);

const getAtMentionRegex = (completedOnly) =>
	completedOnly
		? new RegExp(`(^|[\\s])@([${MENTION_USERNAME_CHARS}]{4,32})(?=\\s)`, "g")
		: new RegExp(
				`(^|[\\s])@([${MENTION_USERNAME_CHARS}]{4,32})(?=\\s|$|[^${MENTION_USERNAME_CHARS}])`,
				"g"
			);

const collectAtMentionMatches = (text, completedOnly) => {
	const matches = [];

	for (const match of text.matchAll(getAtMentionRegex(completedOnly))) {
		const lead = match[1];
		const username = match[2];
		const start = match.index + lead.length;

		matches.push({
			start,
			end: start + username.length + 1,
			username,
		});
	}

	return matches;
};

const collectNamedMentionMatches = (text, mentionNames, blockedRanges) => {
	if (!mentionNames.length) {
		return [];
	}

	const pattern = new RegExp(
		`(^|[\\s])(${mentionNames
			.map(escapeRegExp)
			.join("|")})(?=\\s|$|[^${MENTION_USERNAME_CHARS}])`,
		"g"
	);
	const matches = [];

	for (const match of text.matchAll(pattern)) {
		const lead = match[1];
		const username = match[2];
		const start = match.index + lead.length;
		const end = start + username.length;
		const overlaps = blockedRanges.some((range) => start < range.end && end > range.start);

		if (!overlaps) {
			matches.push({ start, end, username });
		}
	}

	return matches;
};

const appendTextSegment = (segments, text) => {
	if (text) {
		segments.push({ type: "text", value: text });
	}
};

export const isValidMentionUsername = (username) => MENTION_USERNAME_PATTERN.test(username ?? "");

export const extractMentionsFromText = (text) => {
	if (!text) {
		return [];
	}

	const mentions = [];
	const seen = new Set();

	for (const match of text.matchAll(MENTION_TOKEN_REGEX)) {
		const username = match[1];

		if (isValidMentionUsername(username) && !seen.has(username)) {
			seen.add(username);
			mentions.push(username);
		}
	}

	return mentions;
};

export const getMentionCandidatesFromMessages = (messages = [], currentUser) => {
	const usernames = new Set();

	messages.forEach((message) => {
		if (message?.username && message.senderType !== "admin") {
			usernames.add(message.username);
		}

		(message?.mentions ?? []).forEach((mention) => {
			const normalized = String(mention).replace(/^@/, "");

			if (normalized) {
				usernames.add(normalized);
			}
		});

		extractMentionsFromText(message?.text).forEach((mention) => {
			usernames.add(mention);
		});
	});

	if (currentUser) {
		usernames.delete(currentUser);
	}

	return Array.from(usernames).sort((a, b) => a.localeCompare(b));
};

export const getActiveMentionState = (text, cursorPosition) => {
	if (typeof cursorPosition !== "number" || cursorPosition < 0) {
		return null;
	}

	const beforeCursor = text.slice(0, cursorPosition);
	const match = beforeCursor.match(MENTION_QUERY_REGEX);

	if (!match || typeof match.index !== "number") {
		return null;
	}

	const token = match[0];
	const query = match[1] ?? "";
	const start = match.index;
	const charBefore = start > 0 ? beforeCursor[start - 1] : " ";

	if (charBefore && !/\s/.test(charBefore)) {
		return null;
	}

	if (isValidMentionUsername(query)) {
		const charAfter = text[cursorPosition];

		if (!charAfter || /\s/.test(charAfter)) {
			return null;
		}
	}

	return {
		query,
		start,
		end: cursorPosition,
		token,
	};
};

export const applyMentionSelection = ({ text, start, end, username }) => {
	const nextText = `${text.slice(0, start)}@${username} ${text.slice(end)}`;
	const nextCursor = start + username.length + 2;

	return { text: nextText, cursor: nextCursor };
};

export const appendUsernameMention = (text, username) => {
	const normalizedUsername = String(username ?? "").replace(/^@/, "");

	if (!normalizedUsername) {
		return text;
	}

	const mention = `@${normalizedUsername}`;
	const trimmed = text.trimEnd();

	if (!trimmed) {
		return `${mention} `;
	}

	if (trimmed.endsWith(mention)) {
		return `${trimmed} `;
	}

	return `${trimmed} ${mention} `;
};

const CHAT_CHANNEL_PREFERENCE_KEY = "chat_preferred_channel";

const getChatChannelPreferenceStorageKey = () => {
	const username = getUser()?.username;

	if (!username) {
		return CHAT_CHANNEL_PREFERENCE_KEY;
	}

	return `${CHAT_CHANNEL_PREFERENCE_KEY}:${username}`;
};

export const getStoredChatChannelId = () => {
	if (typeof window === "undefined") {
		return null;
	}

	return window.localStorage.getItem(getChatChannelPreferenceStorageKey()) || null;
};

export const setStoredChatChannelId = (channelId) => {
	if (typeof window === "undefined" || !channelId) {
		return;
	}

	window.localStorage.setItem(getChatChannelPreferenceStorageKey(), channelId);
};

export const resolvePreferredChatChannelId = (channels = []) => {
	const enabled = channels.filter((channel) => channel.enabled !== false);
	const stored = getStoredChatChannelId();

	if (stored && enabled.some((channel) => channel.channelId === stored)) {
		return stored;
	}

	return enabled[0]?.channelId ?? "";
};

export const CHAT_TIP_PREFILL_KEY = "chat_tip_prefill";

export const setChatTipPrefill = ({ friendId, username }) => {
	sessionStorage.setItem(
		CHAT_TIP_PREFILL_KEY,
		JSON.stringify({
			method: "SendToFriend",
			friendId: friendId ?? null,
			username: username ?? null,
		})
	);
};

export const getChatTipPrefill = () => {
	const raw = sessionStorage.getItem(CHAT_TIP_PREFILL_KEY);

	if (!raw) {
		return null;
	}

	try {
		return JSON.parse(raw);
	} catch {
		return null;
	}
};

export const clearChatTipPrefill = () => {
	sessionStorage.removeItem(CHAT_TIP_PREFILL_KEY);
};

export const getMessageFriendId = (message) =>
	message?.userId ?? message?.friendId ?? message?.senderId ?? null;

export const normalizeMentionInput = (text, cursorPosition) => {
	const state = getActiveMentionState(text, cursorPosition);

	if (!state) {
		return { text, cursor: cursorPosition };
	}

	const sanitizedQuery = state.query.replace(/[^a-zA-Z0-9_-]/g, "");

	if (sanitizedQuery.length <= 32) {
		if (sanitizedQuery === state.query) {
			return { text, cursor: cursorPosition };
		}

		const nextText = `${text.slice(0, state.start + 1)}${sanitizedQuery}${text.slice(
			state.end
		)}`;
		const removedChars = state.query.length - sanitizedQuery.length;
		const nextCursor = Math.max(state.start + 1, cursorPosition - removedChars);

		return { text: nextText, cursor: nextCursor };
	}

	const mentionQuery = sanitizedQuery.slice(0, 32);
	const overflow = sanitizedQuery.slice(32);
	const mentionText = `@${mentionQuery} `;
	const nextText = `${text.slice(0, state.start)}${mentionText}${overflow}${text.slice(
		state.end
	)}`;
	const nextCursor = state.start + mentionText.length + overflow.length;

	return { text: nextText, cursor: nextCursor };
};

export const parseMentionSegments = (text, options = {}) => {
	const normalizedOptions = Array.isArray(options) ? { mentions: options } : options;
	const { mentions = [], completedOnly = false } = normalizedOptions;

	if (!text) {
		return [];
	}

	const atMatches = collectAtMentionMatches(text, completedOnly);
	const namedMatches = collectNamedMentionMatches(
		text,
		normalizeMentionNames(mentions),
		atMatches
	);
	const matches = [...atMatches, ...namedMatches].sort((a, b) => a.start - b.start);
	const segments = [];
	let lastIndex = 0;

	matches.forEach((match) => {
		if (match.start < lastIndex) {
			return;
		}

		appendTextSegment(segments, text.slice(lastIndex, match.start));
		segments.push({
			type: "mention",
			value: text.slice(match.start, match.end),
			username: match.username,
		});
		lastIndex = match.end;
	});

	appendTextSegment(segments, text.slice(lastIndex));

	return segments;
};

export const useIsLoggedIn = () => {
	const authUser = useSelector((state) => state.auth?.user);

	return Boolean(authUser?.username);
};

export const useChatSocketMessages = (channelId) => {
	return useSelector((state) => state.chat?.channelMessages?.[channelId]);
};

export const useChatPanelState = () => {
	return useSelector((state) => ({
		isPanelOpen: state.chat?.isPanelOpen ?? false,
		activeChannelId: state.chat?.activeChannelId,
	}));
};

export const useChatAvailability = () => {
	return useSelector((state) => ({
		isChatEnabled: state.chat?.isChatEnabled !== false,
		eligibilityLoaded: Boolean(state.chat?.eligibilityLoaded),
	}));
};

const getReportTypeLabel = (report) => {
	if (report?.type) {
		return String(report.type);
	}

	if (report?.transactionType) {
		return report.transactionType.split("#")[1] ?? "";
	}

	return "";
};

const getReportTransactionId = (report) => report?.transactionId ?? report?.id ?? null;

export const isSportsbookHistoryReport = (report) => {
	if (report?.couponId) {
		return true;
	}

	const transactionId = getReportTransactionId(report);

	return Boolean(transactionId && String(transactionId).includes("#Payout"));
};

const isWinTypeLabel = (label) => {
	const normalized = String(label ?? "").toLowerCase();

	return normalized === "won" || normalized.includes("win");
};

const isRollbackReport = (report) => {
	const typeLabel = getReportTypeLabel(report);
	return typeLabel.toLowerCase().includes("rollback");
};

const isCasinoWinReport = (report) => {
	const transactionId = getReportTransactionId(report);

	if (!report?.roundId || !transactionId || report?.couponId) {
		return false;
	}

	if (isSportsbookHistoryReport(report)) {
		return false;
	}

	return isWinTypeLabel(getReportTypeLabel(report));
};

const isSportsbookTicketReport = (report) => {
	const transactionId = getReportTransactionId(report);

	if (!transactionId || isRollbackReport(report)) {
		return false;
	}

	if (!isWinTypeLabel(getReportTypeLabel(report))) {
		return false;
	}

	return isSportsbookHistoryReport(report);
};

export const formatShareAmount = (amount, currency) => {
	if (amount == null || amount === "") {
		return null;
	}

	const normalizedCurrency = String(currency || "").trim();

	if (!normalizedCurrency) {
		return String(amount);
	}

	return formatMoney(amount, normalizedCurrency);
};

export const getShareCurrency = (payload) =>
	payload?.currency ?? payload?.baseCurrency ?? payload?.winCurrency ?? "";

export const getShareGameIconUrl = (payload) =>
	payload?.gameIconUrl ?? payload?.iconUrl ?? payload?.game_icon_url ?? null;

export const getShareGameName = (payload) =>
	payload?.gameName ?? payload?.gameRemoteName ?? payload?.remoteName ?? null;

export const getShareBetAmount = (payload) =>
	payload?.stakeAmount ?? payload?.stake ?? payload?.betAmount ?? payload?.bet ?? null;

export const getShareWinAmount = (payload) =>
	payload?.winningAmount ?? payload?.winAmount ?? payload?.amount ?? payload?.payout ?? null;

export const formatShareSelectionType = (value) => {
	if (value == null || value === "") {
		return null;
	}

	const normalized = String(value);

	return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export const getSportsbookTicketDetails = (payload) => {
	const { ticketSummary } = payload || {};
	const currency = getShareCurrency(payload);
	const selectionType = formatShareSelectionType(
		payload?.selectionType ?? ticketSummary?.type ?? null
	);
	const selectionCount = payload?.selectionCount ?? ticketSummary?.selectionCount ?? null;

	return {
		selectionType,
		selectionCount,
		stakeAmount: formatShareAmount(getShareBetAmount(payload), currency),
		winningAmount: formatShareAmount(getShareWinAmount(payload), currency),
		totalOdds: payload?.totalOdds ?? ticketSummary?.totalOdds ?? null,
		couponId: payload?.couponId ?? null,
	};
};

export const getChatShareFromHistoryReport = (report) => {
	const transactionId = getReportTransactionId(report);

	if (!transactionId) {
		return null;
	}

	if (isSportsbookTicketReport(report)) {
		return {
			transactionId,
			shareType: "sportsbook_ticket",
		};
	}

	if (isCasinoWinReport(report)) {
		return {
			transactionId,
			shareType: "casino_win",
		};
	}

	return null;
};

export const REPLYABLE_CONTENT_TYPES = new Set(["text", "image", "gif"]);
export const REPLY_SNIPPET_MAX_LENGTH = 80;

export const isReplyableMessage = (message) => {
	if (!message) {
		return false;
	}

	if (message.senderType === "admin" || message.contentType === "admin") {
		return false;
	}

	if (
		message.contentType === "casino_win" ||
		message.contentType === "sportsbook_ticket" ||
		message.sharePayload
	) {
		return false;
	}

	return REPLYABLE_CONTENT_TYPES.has(message.contentType);
};

export const getReplySnippet = (replyTarget, translate) => {
	if (!replyTarget) {
		return "";
	}

	if (replyTarget.removed) {
		return translate("chat.reply.messageRemoved");
	}

	const text = replyTarget.text?.trim();

	if (text) {
		if (text.length > REPLY_SNIPPET_MAX_LENGTH) {
			return `${text.slice(0, REPLY_SNIPPET_MAX_LENGTH)}…`;
		}

		return text;
	}

	if (replyTarget.contentType === "image" || replyTarget.contentType === "gif") {
		return translate("chat.reply.photo");
	}

	return "";
};

export const buildReplyTargetFromMessage = (message) => {
	if (!message?.messageId) {
		return null;
	}

	return {
		messageId: message.messageId,
		username: message.username,
		contentType: message.contentType,
		text: message.text ?? null,
		mediaUrl: message.mediaUrl ?? null,
		removed: false,
	};
};

export const getChatErrorKey = (error) => {
	const code = error?.data?.code ?? error?.data?.errorCode ?? error?.data?.message;

	switch (code) {
		case "ChatBanned":
			return "chat.error.banned";
		case "ChatNotEligible":
			return "chat.error.notEligible";
		case "ChatDisabled":
			return "chat.error.disabled";
		case "BannedWordViolation":
		case "BannedWordDetected":
			return "chat.error.bannedWord";
		case "MessageTooLong":
			return "chat.error.tooLong";
		case "Unauthorized":
			return "chat.error.unauthorized";
		case "InvalidParam":
			return "chat.error.generic";
		case "InvalidReplyTarget":
			return "chat.error.invalidReplyTarget";
		default:
			if (error?.status === 401) {
				return "chat.error.unauthorized";
			}
			return "chat.error.generic";
	}
};

export const getChatBannedWordViolations = (error) => {
	const violations = error?.data?.violations;

	if (!Array.isArray(violations)) {
		return [];
	}

	return violations.map((violation) => violation?.word).filter(Boolean);
};

const OBFUSCATION_SYMBOL = "*";

const getReplaceCount = (length) => {
	if (length <= 4) {
		return 1;
	}

	if (length <= 8) {
		return 2;
	}

	return 3;
};

const getReplacementIndices = (characters, replaceCount) => {
	const letterIndices = characters.reduce((indices, character, index) => {
		if (/[a-z0-9]/i.test(character)) {
			indices.push(index);
		}

		return indices;
	}, []);

	if (!letterIndices.length) {
		return [];
	}

	const vowelIndices = letterIndices.filter((index) => /[aeiou]/i.test(characters[index]));
	const candidateIndices = vowelIndices.length ? vowelIndices : letterIndices;

	if (replaceCount === 1) {
		return [candidateIndices[Math.floor(candidateIndices.length / 2)]];
	}

	const selected = new Set();
	const step = Math.max(1, Math.floor(candidateIndices.length / replaceCount));

	for (let index = 0; index < replaceCount && selected.size < replaceCount; index += 1) {
		selected.add(candidateIndices[Math.min(index * step, candidateIndices.length - 1)]);
	}

	for (const index of letterIndices) {
		if (selected.size >= replaceCount) {
			break;
		}

		selected.add(index);
	}

	return Array.from(selected).slice(0, replaceCount);
};

export const obfuscateBannedWord = (word) => {
	const source = String(word ?? "");

	if (!source) {
		return "";
	}

	const characters = source.split("");
	const replacementIndices = new Set(
		getReplacementIndices(characters, getReplaceCount(characters.length))
	);

	return characters
		.map((character, index) => (replacementIndices.has(index) ? OBFUSCATION_SYMBOL : character))
		.join("");
};

export const getChatErrorMessage = (error, translate) => {
	const code = error?.data?.code ?? error?.data?.errorCode;
	const apiMessage = error?.data?.message;

	if (code === "InvalidReplyTarget" && apiMessage) {
		return apiMessage;
	}

	const violations = getChatBannedWordViolations(error);

	if ((code === "BannedWordViolation" || code === "BannedWordDetected") && violations.length) {
		const obscuredWords = violations.map((word) => obfuscateBannedWord(word)).join(", ");

		return translate("chat.error.bannedWordList", {
			words: obscuredWords,
		});
	}

	return translate(getChatErrorKey(error));
};

const ALLOWED_MEDIA_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

export const isAllowedChatMediaType = (type) => ALLOWED_MEDIA_TYPES.includes(type);

const CHAT_PORTAL_ID = "chat-portal";

export const getChatPortal = () => {
	if (typeof document === "undefined") {
		return null;
	}

	let portal = document.getElementById(CHAT_PORTAL_ID);

	if (!portal) {
		portal = document.createElement("div");
		portal.id = CHAT_PORTAL_ID;
		document.body.appendChild(portal);
	}

	return portal;
};
